import React from "react";
import { Users2, TrendingUp } from "lucide-react";
import * as L from "lucide-react";
import LiveCampaignDashboard from "./LiveCampaignDashboard";
import { useContent } from "@/lib/cms";

const FALLBACK = {
  eyebrow: "Bulk messaging",
  heading: "Broadcast to millions, feel personal to each one",
  description:
    "Segment audiences, personalise with variables, schedule across time zones and watch revenue soar — all from one campaign console.",
  feature_left: {
    icon: "Users2",
    title: "Smart segments",
    desc: "Filter by behaviour, geography, lifecycle stage.",
    chips: ["Geography", "Behaviour", "Lifecycle", "Customer tags"],
  },
  feature_right: {
    icon: "TrendingUp",
    title: "Deliverability that works",
    desc: "Average 98% delivery vs 18% on email.",
    stats: [
      { v: "98%", l: "Avg delivery" },
      { v: "99%", l: "Best campaign" },
      { v: "Live", l: "Real-time" },
      { v: "Meta", l: "Optimised" },
    ],
  },
};

export const Bulk = () => {
  const { data, enabled } = useContent("campaign_analytics", FALLBACK);
  if (!enabled) return null;
  const c = { ...FALLBACK, ...(data || {}) };
  const left = { ...FALLBACK.feature_left, ...(c.feature_left || {}) };
  const right = { ...FALLBACK.feature_right, ...(c.feature_right || {}) };
  const LeftIcon = L[left.icon] || Users2;
  const RightIcon = L[right.icon] || TrendingUp;

  return (
    <section className="py-20 sm:py-28 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        {/* Left: live campaign console */}
        <LiveCampaignDashboard content={c} />

        {/* Right: heading & feature cards */}
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">{c.eyebrow}</p>
          <h2
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {c.heading}
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-xl">{c.description}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {/* Left feature card with chips */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(2,6,23,0.04)] transition-all">
              <LeftIcon className="h-5 w-5 text-blue-600" />
              <div className="mt-3 font-semibold text-slate-900">{left.title}</div>
              <div className="text-sm text-slate-600 mt-1">{left.desc}</div>
              {Array.isArray(left.chips) && left.chips.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {left.chips.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-100"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Right feature card with stats */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(2,6,23,0.04)] transition-all">
              <RightIcon className="h-5 w-5 text-emerald-600" />
              <div className="mt-3 font-semibold text-slate-900">{right.title}</div>
              <div className="text-sm text-slate-600 mt-1">{right.desc}</div>
              {Array.isArray(right.stats) && right.stats.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {right.stats.map((s, idx) => (
                    <div key={`${s.l}-${idx}`} className="flex items-baseline gap-1.5 min-w-0">
                      <span className="text-[12px] font-extrabold text-emerald-700 leading-none">{s.v}</span>
                      <span className="text-[10.5px] text-slate-500 truncate">{s.l}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
